import { useDroppable } from "@dnd-kit/core";
import type { OrderListItem } from "@school-sales-ai/shared";
import type { OrderBoardColumn } from "@/lib/order-board";
import { OrderKanbanCard } from "./OrderKanbanCard";

type Props = {
  column: OrderBoardColumn;
  orders: OrderListItem[];
  /** The column's true total from the server — `orders` is only the loaded page. */
  count: number;
  activeId: string | null;
  /**
   * While a card is being dragged: whether it may land here. `undefined` when
   * nothing is dragged. A column it may not reach is dimmed, so the rule is
   * visible before the drop rather than only as a toast after it.
   */
  accepts?: boolean;
  onLoadMore?: () => void;
  loadMoreCount?: number;
  onSeeAll?: () => void;
};

export function OrderKanbanColumn({
  column,
  orders,
  count,
  activeId,
  accepts,
  onLoadMore,
  loadMoreCount,
  onSeeAll,
}: Props): React.JSX.Element {
  const { setNodeRef, isOver } = useDroppable({ id: column.key });
  const hasMore = orders.length < count;
  const isSubStage = column.stageId !== null;

  return (
    <div
      className={[
        "flex h-full min-h-0 w-64 flex-shrink-0 flex-col gap-2 transition-opacity",
        accepts === false ? "opacity-40" : "",
      ].join(" ")}
    >
      <div className="flex items-center justify-between px-1">
        <span className="flex min-w-0 items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink-600">
          <span className={`h-2 w-2 shrink-0 rounded-full ${column.swatchClass}`} aria-hidden />
          {/* A custom column is a step inside "Por entregar"; saying so keeps
              the board honest about what status the order actually has. */}
          {isSubStage && <span className="text-ink-300 normal-case">↳</span>}
          <span className="truncate">{column.label}</span>
        </span>
        <span className="rounded-full bg-ink-100 px-2 py-0.5 text-xs font-semibold text-ink-600">
          {count}
        </span>
      </div>

      <div
        ref={setNodeRef}
        className={[
          "flex min-h-[120px] flex-1 flex-col gap-2 overflow-y-auto rounded-xl p-2 transition-colors",
          isOver && accepts !== false
            ? "bg-lime-pale outline-dashed outline-2 outline-moss"
            : "bg-ink-050",
        ].join(" ")}
      >
        {orders.map((order) => (
          <OrderKanbanCard key={order.id} order={order} isDragging={activeId === order.id} />
        ))}

        {orders.length === 0 && (
          <div className="flex flex-1 items-center justify-center py-6">
            <span className="text-xs text-ink-200">Sin pedidos</span>
          </div>
        )}

        {hasMore && (
          <div className="flex flex-col items-center gap-1.5 py-2">
            <span className="text-[10px] text-ink-300">
              {orders.length} de {count}
            </span>
            {/* An explicit click, never a scroll sentinel — see KanbanColumn. */}
            {onLoadMore && (
              <button
                type="button"
                onClick={onLoadMore}
                className="cursor-pointer rounded-md border border-ink-200 bg-white px-2 py-1 text-[11px] font-semibold text-ink-700 hover:bg-ink-050"
              >
                Ver {loadMoreCount} más
              </button>
            )}
            {onSeeAll && (
              <button
                type="button"
                onClick={onSeeAll}
                className="cursor-pointer text-[10px] text-ink-400 hover:text-moss hover:underline"
              >
                Ver todos en Tabla →
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
